/**
 * Media rescue — UI layer.
 *
 * Deliberately two separate folder choices: the ailing one is opened READ-ONLY,
 * and survivors go somewhere else the user picks. Writing rescued files back
 * onto the drive they came from is the one action that can finish it off, so the
 * interface never offers it.
 */

import { walk, rescueAll, pickerSupported, FOUND } from './rescue-core.js';

const T = (k, p) => {
  let s = (window.__MR_I18N && window.__MR_I18N[k]) || k;
  // Longest placeholder first, so `:n` can never eat the head of a `:name`.
  if (p) for (const a of Object.keys(p).sort((x, y) => y.length - x.length)) {
    s = s.split(':' + a).join(p[a]);
  }
  return s;
};
const el = (id) => document.getElementById(id);
const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

function human(bytes) {
  const u = [T('unit_b'), T('unit_kb'), T('unit_mb'), T('unit_gb')];
  let n = Number(bytes) || 0, i = 0;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n < 10 && i ? n.toFixed(1) : Math.round(n)} ${u[i]}`;
}

let state = { source: null, listing: null, running: false, abort: null };

function show(html) { const o = el('mr-out'); o.innerHTML = html; o.hidden = false; }
const card = (tone, title, body, extra = '') =>
  `<div class="mr-card mr-tone-${tone}"><p class="mr-title">${title}</p>
     <p class="mr-body">${body}</p>${extra}</div>`;

/* Dropping the ailing folder onto the page is the same gesture as picking it,
 * and used to be answered with «это папка, а не файл — перетащите нужный файл»
 * by the shared off-zone handler, on a tool whose only input is a folder. The
 * layout hands the directory handle over when a page declares this hook. */
window.__acceptDroppedDirectory = (handle) => { scanSource(handle); };

async function pickSource() {
  if (!pickerSupported()) {
    show(card('warn', T('verdict_unsupported'), T('body_unsupported')));
    /* How many people arrive on a browser that can never run this. If the
     * number is large the answer is not a better message — it is a different
     * entry point (multi-file selection), which iOS does support. */
    window.__aeDiagnostic && window.__aeDiagnostic({ issue: 'no_directory_picker' });
    return;
  }
  let dir;
  try {
    dir = await window.showDirectoryPicker({ mode: 'read', id: 'rescue-source' });
  } catch (_) { return; }                   // the user closed the picker

  return scanSource(dir);
}

async function scanSource(dir) {
  state.source = dir;
  show(`<div class="mr-working">${T('scanning')}</div>`);
  window.__stage && window.__stage('scanning');

  let listing;
  try {
    listing = await walk(dir, {
      onEntry: ({ count }) => {
        const w = document.querySelector('.mr-working');
        if (w && count % 25 === 0) w.textContent = T('scanning_n', { n: String(count) });
      },
    });
  } catch (err) {
    show(card('bad', T('verdict_scan_failed'), T('body_scan_failed')));
    // A drive that will not even list is the headline failure of this tool;
    // without an event it is indistinguishable from nobody trying.
    window.__aeDiagnostic && window.__aeDiagnostic({ issue: 'scan_failed' });
    return;
  }

  state.listing = listing;
  const total = listing.files.reduce((n, f) => n + f.size, 0);

  const rows = [
    [T('stat_files'), String(listing.files.length)],
    [T('stat_bytes'), human(total)],
    listing.failedDirs.length ? [T('stat_bad_dirs'), String(listing.failedDirs.length)] : null,
    listing.failedStat.length ? [T('stat_bad_stat'), String(listing.failedStat.length)] : null,
  ].filter(Boolean).map(([k, v]) => `<tr><th scope="row">${k}</th><td>${v}</td></tr>`).join('');

  const trouble = [...listing.failedDirs.map((p) => [p, T('trouble_dir')]),
                   ...listing.failedStat.map((p) => [p, T('trouble_stat')])];
  const troubleHtml = trouble.length
    ? `<section class="mr-block"><h3>${T('trouble_title', { n: String(trouble.length) })}</h3>
         <p class="mr-body">${T('trouble_desc')}</p>
         <ul class="mr-list">${trouble.slice(0, 200).map(([p, why]) =>
           `<li><code>${esc(p)}</code> — ${why}</li>`).join('')}</ul></section>`
    : '';

  show(card(listing.files.length ? 'ok' : 'warn',
      T('verdict_scanned'), T('body_scanned', { n: String(listing.files.length), size: human(total) }),
      listing.files.length
        ? `<div class="mr-actions"><button type="button" class="mr-btn" id="mr-run">${T('start')}</button></div>`
        : '')
    + `<section class="mr-block"><h3>${T('stats_title')}</h3><table class="mr-stats">${rows}</table></section>`
    + troubleHtml
    + askSection({ compact: true }));

  const run = el('mr-run');
  if (run) run.addEventListener('click', start);
}

async function start() {
  if (state.running) return;
  const files = state.listing.files;

  /* A separate destination, chosen explicitly. The source stays read-only: a
   * drive that is failing is a drive whose every write may be its last. */
  let dest = null;
  try {
    dest = await window.showDirectoryPicker({ mode: 'readwrite', id: 'rescue-dest' });
  } catch (_) { return; }

  /* The unit is the RESCUE, not the file. Passing per-file keys here would cost
   * one unit per file, and a drive holds thousands — the cap would refuse the
   * only run the tool exists for. Scanning stays free: it is diagnosis. */
  const _run = window.__toolLimit ? window.__toolLimit.begin('media-rescue') : null;
  if (window.__toolLimit && !_run) return;

  state.running = true;
  state.abort = new AbortController();
  show(`<div class="mr-card mr-tone-ok"><p class="mr-title">${T('verdict_running')}</p>
      <p class="mr-body" id="mr-now">…</p>
      <div class="mr-bar"><div class="mr-bar-fill" id="mr-bar" style="width:0%"></div></div>
      <div class="mr-actions"><button type="button" class="mr-btn mr-btn-ghost" id="mr-stop">${T('stop')}</button></div>
    </div>`);
  el('mr-stop').addEventListener('click', () => state.abort.abort());

  let done = 0;
  const write = async (path, bytes) => {
    // Recreate the source's folder structure under the destination.
    const parts = path.split('/');
    let dir = dest;
    for (const seg of parts.slice(0, -1)) dir = await dir.getDirectoryHandle(seg, { create: true });
    const fh = await dir.getFileHandle(parts[parts.length - 1], { create: true });
    const w = await fh.createWritable();
    await w.write(bytes);
    await w.close();
    if (window.__deliveredToDisk) window.__deliveredToDisk(path, bytes, 'folder');
  };

  /* The file inspector is fetched only now — after the person has chosen a
   * source AND a destination, i.e. once they are certainly running a rescue.
   * It is ~140 KB of signature and structure tables that a visitor who only
   * reads the page must not pay for. If it fails to arrive the rescue still
   * runs: inspection enriches the report, it never gates the copy. */
  let inspect = null;
  try {
    const fd = await import('../file-doctor/diagnose.js');
    inspect = (bytes, path) => fd.diagnose(bytes, path.split('/').pop(), {});
  } catch (_) {
    window.__ae && window.__ae('feature_use', { slug: 'media-rescue', f: 'inspector_unavailable' });
  }

  const report = await rescueAll(files, write, {
    signal: state.abort.signal,
    backoffMs: 120,
    inspect,
    onFile: (info) => {
      if (info.phase !== 'reading') done++;
      const now = el('mr-now'), bar = el('mr-bar');
      if (now) now.textContent = T('now', { path: info.path, done: String(done), total: String(files.length) });
      if (bar) bar.style.width = Math.round(done / files.length * 100) + '%';
    },
  });

  state.running = false;
  if (report.ok.length || report.partial.length) _run && _run.commit(); else _run && _run.abort();
  show(renderReport(report, files.length));
  wireAsk();
  /* `found` is the number this tool had no way of reporting before, and the
   * reason the complaint could not be measured: a run where everything copied
   * and nothing was wrong looked exactly like a run where everything copied
   * and half of it was husks. */
  const tally = countFound(report);
  window.__aeToolComplete && window.__aeToolComplete({
    files: files.length, ok: report.ok.length, partial: report.partial.length,
    lost: report.lost.length, bytes: report.bytes,
    found: report.found.length, empty: tally.empty, blank: tally.blank,
    broken: tally.damaged, nameless: tally.nameless,
    inspected: inspect ? 1 : 0 });
}

/** How many of each finding — the tally both the report and telemetry use. */
function countFound(report) {
  const t = { empty: 0, blank: 0, damaged: 0, nameless: 0 };
  for (const r of report.found || []) if (t[r.found] != null) t[r.found]++;
  return t;
}

const url = (slug) => (window.__MR_URLS || {})[slug] || '';

/**
 * The section this tool was missing: what it CANNOT see.
 *
 * `showDirectoryPicker` hands over the live directory, so a file the file
 * system no longer lists — deleted, or lost when the table was damaged — is
 * invisible here no matter how well the copy goes. Saying so is not a
 * disclaimer, it is the answer to the only question the person actually asked,
 * and the reason two of the two people who ever rated this tool did so with one
 * star immediately after a flawless run.
 *
 * The next step is spelled out to the byte because that is where it used to
 * die: /disk-recovery works on an IMAGE, and of the 29 files brought to it in a
 * month, 26 were not images at all. "Make an image first" is useless advice
 * without the command that makes one.
 */
function askSection(opts) {
  const compact = !!(opts && opts.compact);
  const cmd = (label, text) =>
    `<p class="mr-body" style="margin-top:10px"><strong>${label}</strong></p>
     <pre class="mr-cmd"><code>${esc(text)}</code></pre>`;
  const win = navigator.platform && /win/i.test(navigator.platform);
  const mac = navigator.platform && /mac/i.test(navigator.platform);
  const blocks = [
    [win || (!win && !mac), T('image_win'), T('image_win_cmd')],
    [mac || !win, T('image_nix'), T('image_nix_cmd')],
  ];
  const body = `<p class="mr-body">${T('ask_body')}</p>
      <p class="mr-body" style="margin-top:12px"><strong>${T('image_title')}</strong></p>
      ${blocks.filter(([show]) => show).map(([, l, c]) => cmd(l, c)).join('')}
      <p class="mr-body">${T('image_note')}</p>
      ${url('disk-recovery')
        ? `<div class="mr-actions" style="justify-content:flex-start"><a class="mr-btn" href="${esc(url('disk-recovery'))}">${T('image_next')}</a></div>`
        : ''}`;

  /* Compact form for the PLAN screen — the question belongs BEFORE the copy,
   * not only after it. The person who wrote the complaint spent 29 minutes
   * copying 4.3 GB and only then found out this tool could never see what she
   * was looking for; a folded question at the moment the file list appears is
   * that half hour back. `<details>` and not a script: it must work the same
   * whether or not anything else on the page does. */
  if (compact) {
    return `<details class="mr-block mr-ask" id="mr-ask">
        <summary class="mr-ask-q">${T('ask_title')}</summary>
        ${body}
      </details>`;
  }
  return `<section class="mr-block mr-ask" id="mr-ask">
      <h3>${T('ask_title')}</h3>
      ${body}
    </section>`;
}

/* The CTA only ever moves the page to a section that is already rendered, and
 * with `block:'nearest'` — the answer must not jump away from the question. */
function wireAsk() {
  const btn = el('mr-ask-btn'), ask = el('mr-ask');
  if (!btn || !ask) return;
  btn.addEventListener('click', () => {
    ask.classList.add('mr-ask-lit');
    ask.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    window.__ae && window.__ae('feature_use', { slug: 'media-rescue', f: 'not_in_the_list' });
  });
}

/** One line in the findings list: what is wrong, and where it can be taken. */
function foundRow(r) {
  const where = r.repair && url(r.repair)
    ? ` <a href="${esc(url(r.repair))}">${T('found_take_it')}</a>` : '';
  if (r.found === FOUND.EMPTY) return `<li><code>${esc(r.path)}</code> — ${T('found_empty')}</li>`;
  if (r.found === FOUND.BLANK) return `<li><code>${esc(r.path)}</code> — ${T('found_blank', { size: human(r.size) })}</li>`;
  if (r.found === FOUND.NAMELESS) return `<li><code>${esc(r.path)}</code> — ${T('found_nameless', { label: r.label || r.ext, ext: r.ext })}</li>`;
  return `<li><code>${esc(r.path)}</code> — ${T('why_' + r.why) || T('why_damaged')}${where}</li>`;
}

function renderReport(report, total) {
  const tally = countFound(report);
  const damagedFound = report.found.length;

  /* The verdict answers the person's question, not the loop's.
   *
   * "Копирование закончено" used to be printed over every outcome, including
   * the one that means "there was nothing here to rescue" — 81 % of all runs in
   * production. A green card over that is a tool congratulating itself for an
   * errand it did not run. */
  const salvaged = report.partial.length > 0;
  const nothingWrong = !salvaged && !report.lost.length && !damagedFound;
  const tone = nothingWrong ? 'warn'
    : report.lost.length ? (report.ok.length || salvaged ? 'warn' : 'bad')
      : damagedFound ? 'warn' : 'ok';

  const rows = [
    [T('stat_ok'), String(report.ok.length)],
    report.partial.length ? [T('stat_partial'), String(report.partial.length)] : null,
    report.lost.length ? [T('stat_lost'), String(report.lost.length)] : null,
    damagedFound ? [T('stat_found'), String(damagedFound)] : null,
    [T('stat_rescued_bytes'), human(report.bytes)],
  ].filter(Boolean).map(([k, v]) => `<tr><th scope="row">${k}</th><td>${v}</td></tr>`).join('');

  const list = (items, title, desc, render) => items.length
    ? `<section class="mr-block"><h3>${title}</h3><p class="mr-body">${desc}</p>
         <ul class="mr-list">${items.slice(0, 300).map(render).join('')}</ul></section>`
    : '';

  const head = nothingWrong
    ? card(tone, T('verdict_nothing_damaged'),
        T('body_nothing_damaged', { n: String(report.ok.length), bytes: human(report.bytes) }),
        `<div class="mr-actions"><button type="button" class="mr-btn" id="mr-ask-btn">${T('ask_cta')}</button></div>`)
    : card(tone, T('verdict_finished'),
        T('body_finished', { ok: String(report.ok.length), total: String(total), bytes: human(report.bytes) }));

  return head
    + `<section class="mr-block"><h3>${T('stats_title')}</h3><table class="mr-stats">${rows}</table></section>`
    + list(report.found, T('found_title', { n: String(damagedFound) }), T('found_desc'), foundRow)
    + list(report.partial, T('partial_title', { n: String(report.partial.length) }), T('partial_desc'),
        (r) => `<li><code>${esc(r.path)}</code> — ${T('partial_row', {
          salvaged: human(r.salvaged), size: human(r.size), holes: String(r.badBlocks) })}</li>`)
    + list(report.lost, T('lost_title', { n: String(report.lost.length) }), T('lost_desc'),
        (r) => `<li><code>${esc(r.path)}</code></li>`)
    + askSection();
}

document.addEventListener('DOMContentLoaded', () => {
  const btn = el('mr-pick');
  if (!btn) return;
  if (!pickerSupported()) {
    btn.disabled = true;
    show(card('warn', T('verdict_unsupported'), T('body_unsupported')));
    /* How many people arrive on a browser that can never run this. If the
     * number is large the answer is not a better message — it is a different
     * entry point (multi-file selection), which iOS does support. */
    window.__aeDiagnostic && window.__aeDiagnostic({ issue: 'no_directory_picker' });
    return;
  }
  btn.addEventListener('click', pickSource);
});

export { pickSource };
